/**
 * ParentalGate — the lightweight "grown-ups only" math challenge.
 *
 * Distinct from verifiable parental consent: this is just a friction
 * gate before mic, outbound links, and purchases (the Apple-required
 * "are you a grown-up" before sensitive actions). Wrong answers
 * reshuffle the numbers.
 *
 * Usage from a calling component:
 *
 *   const gate = useParentalGate();
 *   // ...trigger
 *   gate.run("mic", () => actuallyDoIt());
 *   // ...render once
 *   {gate.Gate && <gate.Gate />}
 *
 * The handoff insists this NOT be conflated with consent. The backend
 * never sees the math answer — it's a UI gesture.
 */

import { useCallback, useId, useRef, useState } from "react";
import { useGatePresence } from "./gateStack";

export type GateReason = "default" | "mic" | "link" | "purchase" | "share" | "photo";

const COPY: Record<GateReason, { title: string; sub: string }> = {
  default: {
    title: "Ask a grown-up",
    sub: "This part is for grown-ups. Please solve this to continue.",
  },
  mic: {
    title: "Grown-up check",
    sub: "A grown-up needs to turn on the microphone.",
  },
  link: {
    title: "Leaving world by me",
    sub: "A grown-up should okay opening an outside link.",
  },
  purchase: {
    title: "Grown-ups only",
    sub: "Purchases need a grown-up. Solve this to continue.",
  },
  // ipad-native-shell R16.1: the iOS share sheet hands the link to other apps.
  share: {
    title: "Grown-up check",
    sub: "A grown-up should okay sending this link to someone.",
  },
  // game-on-freeform U7: a project's photo leaves the app (share sheet or download).
  photo: {
    title: "Grown-up check",
    sub: "A grown-up should okay saving this picture.",
  },
};

function pick(): number {
  return 2 + Math.floor(Math.random() * 7);
}

interface InternalState {
  open: boolean;
  reason: GateReason;
  pending: (() => void) | null;
  a: number;
  b: number;
  wrong: boolean;
  value: string;
}

const _initial: InternalState = {
  open: false,
  reason: "default",
  pending: null,
  a: 0,
  b: 0,
  wrong: false,
  value: "",
};

export interface UseParentalGateValue {
  run: (reason: GateReason, action: () => void) => void;
  Gate: (() => JSX.Element | null) | null;
}

export function useParentalGate(): UseParentalGateValue {
  const [s, setS] = useState<InternalState>(_initial);
  const problemId = useId();

  // Register with the global gate stack so social/friend visibility
  // drops while the math challenge is up.
  useGatePresence(s.open);

  const run = useCallback((reason: GateReason, action: () => void) => {
    setS({
      open: true,
      reason,
      pending: action,
      a: pick(),
      b: pick(),
      wrong: false,
      value: "",
    });
  }, []);

  const close = useCallback(() => {
    setS({ ..._initial });
  }, []);

  const submit = useCallback(() => {
    setS((prev) => {
      const expected = prev.a * prev.b;
      if (parseInt(prev.value, 10) === expected) {
        const action = prev.pending;
        // Defer the action so the modal can close first.
        setTimeout(() => action?.(), 0);
        return { ..._initial };
      }
      return {
        ...prev,
        a: pick(),
        b: pick(),
        wrong: true,
        value: "",
      };
    });
  }, []);

  // The latest state and handlers, read by a Gate whose identity never changes. It used to
  // be a useCallback over `s`, so every keystroke made a new component type and React
  // remounted the input: on the iPad the focus and the key were lost, and "12" never went
  // in (the operator's walk, 2026-10-27; the mic gate is the same component).
  const latest = useRef<GateViewProps>({ s, problemId, close, submit, setValue: () => {} });
  latest.current = {
    s,
    problemId,
    close,
    submit,
    setValue: (value) => setS((p) => ({ ...p, value })),
  };
  const Gate = useCallback((): JSX.Element | null => <GateView {...latest.current} />, []);

  return { run, Gate: s.open ? Gate : null };
}

interface GateViewProps {
  s: InternalState;
  problemId: string;
  close: () => void;
  submit: () => void;
  setValue: (value: string) => void;
}

/** The gate itself, a stable component type: re-rendered, never remounted, as the state moves. */
function GateView({ s, problemId, close, submit, setValue }: GateViewProps): JSX.Element | null {
  if (!s.open) return null;
  const copy = COPY[s.reason];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      className="wbm-parental-gate"
      onClick={close}
    >
      <div
        className="wbm-parental-gate__card"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{copy.title}</h2>
        <p>{copy.sub}</p>
        <p id={problemId} className="wbm-parental-gate__problem">
          What is <strong>{s.a} × {s.b}</strong>?
        </p>
        <input
          type="number"
          inputMode="numeric"
          aria-labelledby={problemId}
          autoFocus
          value={s.value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        {s.wrong && (
          <p role="status" className="wbm-parental-gate__wrong">
            Not quite — try the new one.
          </p>
        )}
        <div className="wbm-parental-gate__actions">
          <button type="button" onClick={submit}>
            Continue
          </button>
          <button type="button" onClick={close}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
